import { useHerdControlAnalytics } from "features/shared/hooks/useHerdControlAnalytics";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { updateConciliationStartDate } from "features/manejo/redux/actions/farmActions";
import {
  activateCategory,
  createCategory,
  deleteCategory,
  fetchAgeRanges,
  fetchEnrichedFarmCategories,
  fetchFarmWeightGainCurves,
  updateCategory,
} from "redux/operations";
import type { RootState } from "redux/reducers";
import CategoryPage from "./categoryPage";

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

type OwnProps = {
  showLotSize?: boolean;
  showWeightGain?: boolean;
  showWeightGainCurve?: boolean;
  showConciliationAmount?: boolean;
};
type CategoryContainerProps = OwnProps & StateProps & DispatchProps;

function CategoryContainer({
  activateCategory,
  ageRanges,
  conciliationStartDate,
  createCategory,
  deleteCategory,
  enrichedFarmCategories,
  farmWeightGainCurves,
  fetchAgeRanges,
  fetchEnrichedFarmCategories,
  fetchFarmWeightGainCurves,
  showConciliationAmount = false,
  showLotSize = false,
  showWeightGain = false,
  showWeightGainCurve = false,
  updateCategory,
  updateConciliationDate,
}: CategoryContainerProps) {
  const { trackEvent } = useHerdControlAnalytics({
    tabName: "Categoria Animal",
  });

  useEffect(() => {
    trackEvent();

    // We want to run this only once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEnrichedFarmCategories();
    fetchAgeRanges();
    fetchFarmWeightGainCurves();

    //  We want to run this only once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateCategory = async (
    ...params: Parameters<typeof createCategory>
  ) => {
    await Promise.resolve(createCategory(...params));
    // Ensure category is created before refetching
    fetchEnrichedFarmCategories();
  };

  const handleUpdateCategory = async (
    ...params: Parameters<typeof updateCategory>
  ) => {
    await Promise.resolve(updateCategory(...params));
    // Ensure category is updated before refetching
    fetchEnrichedFarmCategories();
  };

  const handleUpdateConciliationDate = async (date: string) => {
    updateConciliationDate(date);
  };

  const handleActivateCategory = async (
    ...params: Parameters<typeof activateCategory>
  ) => {
    await Promise.resolve(activateCategory(...params));

    // Ensure category is activated before refetching
    fetchEnrichedFarmCategories();
  };

  const handleDeleteCategory = async (
    ...params: Parameters<typeof deleteCategory>
  ) => {
    await Promise.resolve(deleteCategory(...params));

    // Ensure category is deleted before refetching
    fetchEnrichedFarmCategories();
  };

  return (
    <CategoryPage
      activateCategory={handleActivateCategory}
      ageRanges={ageRanges}
      enrichedFarmCategories={enrichedFarmCategories}
      createNewCategory={handleCreateCategory}
      conciliationStartDate={conciliationStartDate}
      deleteCategory={handleDeleteCategory}
      fieldsVisibility={{
        showAmountForConciliation: showConciliationAmount,
        showHeadCount: showLotSize,
        showWeightGain,
        showWeightGainCurve,
      }}
      updateCategory={handleUpdateCategory}
      updateConciliationDate={handleUpdateConciliationDate}
      farmWeightGainCurves={farmWeightGainCurves}
    />
  );
}

const mapDispatchToProps = {
  activateCategory,
  createCategory,
  deleteCategory,
  fetchAgeRanges,
  fetchEnrichedFarmCategories,
  fetchFarmWeightGainCurves,
  updateCategory,
  updateConciliationDate: updateConciliationStartDate,
};

const mapStateToProps = (state: RootState) => ({
  ageRanges: state.ageRange.ageRanges,
  conciliationStartDate: state.fazenda.DataInicioConciliacao,
  currentFarm: state.fazenda.currentFarm,
  enrichedFarmCategories: state.category.enrichedFarmCategories,
  farmWeightGainCurves: state.livestock.farmWeightGainCurves,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(CategoryContainer);
